/**
 * Reglas de bloqueos que la web revisa antes de llamar a la API (ADR-0018), para deshabilitar en el buscador lo
 * que la API rechazaría y decir por qué. La API decide igual: si algo se escapa (p. ej. un ciclo a través de
 * tareas que la web no tiene a mano), responde con su error y se muestra en el aviso flotante.
 *
 * Funciones puras: se prueban sin red.
 */
import type { Tarea, TareaDetalle } from '@cdtec-backlog/contrato';

/** Lo que se sabe de una tarea en el grafo: a quiénes espera y de cuándo es ese dato. */
export interface NodoBloqueos {
  /** `actualizadoEn` de la versión de la tarea de donde salen los bloqueantes (ISO, se compara como texto). */
  actualizadoEn: string;
  /** Ids de las tareas que espera (sus bloqueantes, abiertos o cerrados). */
  bloqueantes: readonly number[];
}

/** Para cada tarea conocida, a quiénes espera. */
export type GrafoBloqueos = ReadonlyMap<number, NodoBloqueos>;

type TareaDelGrafo = Pick<Tarea, 'id' | 'bloqueadaPor' | 'actualizadoEn'>;

/** La versión más nueva entre la que ya está en el grafo y `tarea`. A igual fecha se queda la que estaba. */
function masNueva(actual: NodoBloqueos | undefined, tarea: TareaDelGrafo): NodoBloqueos {
  if (actual && actual.actualizadoEn >= tarea.actualizadoEn) return actual;
  return { actualizadoEn: tarea.actualizadoEn, bloqueantes: tarea.bloqueadaPor.map((b) => b.id) };
}

/**
 * Arma el grafo «espera a» con las tareas que la web tiene a mano (las listas en caché y los resultados del
 * buscador). Una misma tarea puede venir de varias listas, pedidas en momentos distintos: se queda la versión
 * con el `actualizadoEn` más nuevo (agregar o quitar un bloqueo lo actualiza). Unirlas mezclaría un bloqueo ya
 * quitado con los de ahora y podría marcar como ciclo una opción válida.
 */
export function grafoDeBloqueos(tareas: Iterable<TareaDelGrafo>): Map<number, NodoBloqueos> {
  const grafo = new Map<number, NodoBloqueos>();
  for (const tarea of tareas) grafo.set(tarea.id, masNueva(grafo.get(tarea.id), tarea));
  return grafo;
}

/**
 * ¿Cerraría un ciclo «`bloqueadaId` bloqueada por `bloqueanteId`»? Sí si `bloqueanteId` ya espera, directa o
 * indirectamente, a `bloqueadaId`. Recorre el grafo conocido (como la API, cuenta también los bloqueantes
 * cerrados: la relación sigue). Si el grafo está incompleto puede decir que no y la API lo rechaza después.
 */
export function formariaCiclo(
  bloqueadaId: number,
  bloqueanteId: number,
  grafo: GrafoBloqueos,
): boolean {
  const vistas = new Set<number>();
  const pendientes = [bloqueanteId];
  while (pendientes.length > 0) {
    const actual = pendientes.pop() as number;
    if (actual === bloqueadaId) return true;
    if (vistas.has(actual)) continue;
    vistas.add(actual);
    pendientes.push(...(grafo.get(actual)?.bloqueantes ?? []));
  }
  return false;
}

/** La tarea de la ficha, con lo que trae el detalle. */
export type TareaConBloqueos = Pick<TareaDetalle, 'id' | 'padre' | 'bloqueadaPor' | 'bloqueaA'>;

/**
 * Por qué `candidata` no puede bloquear a `tarea`, o `null` si puede. «Es esta tarea» lo pone el buscador.
 *
 * La candidata entra al grafo si su versión es la más nueva. Quienes esperan a esta tarea según el detalle
 * (`bloqueaA`) se suman solo si el grafo no las conoce: si las conoce, manda su versión de las listas (el
 * detalle no trae la fecha de esas filas para decidir cuál es más nueva). Ante la duda se prefiere no marcar
 * un ciclo: la API lo rechaza igual.
 */
export function motivoNoBloqueante(
  tarea: TareaConBloqueos,
  candidata: Pick<Tarea, 'id' | 'padre' | 'bloqueadaPor' | 'actualizadoEn'>,
  grafo: GrafoBloqueos,
): string | null {
  if (tarea.bloqueadaPor.some((b) => b.id === candidata.id)) return 'Ya la bloquea';
  if (tarea.padre?.id === candidata.id || candidata.padre?.id === tarea.id)
    return 'Es su madre o su hija';
  const completo = new Map(grafo);
  completo.set(candidata.id, masNueva(grafo.get(candidata.id), candidata));
  for (const espera of tarea.bloqueaA) {
    if (!completo.has(espera.id))
      completo.set(espera.id, { actualizadoEn: '', bloqueantes: [tarea.id] });
  }
  if (formariaCiclo(tarea.id, candidata.id, completo)) return 'Formaría un ciclo';
  return null;
}
